import { apiClient } from '@/shared/services/api/client'
import { API_ENDPOINTS } from '@/shared/services/api/endpoints'
import type { MetricResponse, UserListData } from '@/modules/operations-management/types'
import type { MetricResponse, UserListData } from '@/modules/operations-management/types'
import type {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  User,
  ProfileUpdateData,
  PasswordChangeData,
  SetCredentialsData,
  LinkOAuthData,
  ApiKey,
  ApiKeyCreateData,
  ApiKeyCreateResponse,
  ApiKeyUpdateResponse,
  ApiKeyListResponse,
  ApiKeyUsageResponse,
  Team,
  TeamCreateData,
  TeamUpdateData,
  TeamDetail,
  TeamMember,
  TeamMemberAddData,
  TeamMemberUpdateData,
  TeamStats,
  TeamBatchAddMembersRequest,
  TeamBatchAddMembersResponse,
  BatchLookupResponse,
  PermissionGrantData,
  PermissionCheckData,
  PermissionCheckResponse,
  UserPermissionResponse,
  TeamPermissionResponse,
  UserListParams,
  PaginatedResponse,
  OAuthURLResponse,
  OAuthCallbackData,
  OAuthProvider,
} from '../types'

const normalizeArrayParam = <T>(value?: T | T[]): T[] | undefined => {
  if (value === undefined || value === null) {
    return undefined
  }
  return Array.isArray(value) ? value : [value]
}

const serializeParamList = <T>(
  value?: T | T[],
  formatter?: (item: T) => string
): string | undefined => {
  const list = normalizeArrayParam(value)
  if (!list || list.length === 0) {
    return undefined
  }
  const items = formatter ? list.map((item) => formatter(item)) : (list as string[])
  return items.join(',')
}

export const userApi = {
  // === 认证相关 ===
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(API_ENDPOINTS.USER.LOGIN, credentials)
    return response.data
  },

  async register(userData: RegisterData): Promise<User> {
    const response = await apiClient.post<User>(API_ENDPOINTS.USER.REGISTER, userData)
    return response.data
  },

  async getOAuthUrl(provider: string, redirectUri?: string): Promise<OAuthURLResponse> {
    // 把当前平台地址一并上报，后端用「平台地址 + 配置后缀」拼出完整回调地址，
    // 这样用 IP 访问就回调 IP，用域名访问就回调域名。
    const params: Record<string, string> = {
      base_url: window.location.origin,
    }
    if (redirectUri) {
      params.redirect_uri = redirectUri
    }
    const response = await apiClient.get<OAuthURLResponse>(API_ENDPOINTS.USER.OAUTH_URL(provider), { params })
    return response.data
  },

  async handleOAuthCallback(provider: string, data: OAuthCallbackData): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(API_ENDPOINTS.USER.OAUTH_CALLBACK(provider), data)
    return response.data
  },

  async getOAuthProviders(): Promise<OAuthProvider[]> {
    const response = await apiClient.get<{ providers: OAuthProvider[], default_provider: string | null }>(API_ENDPOINTS.USER.OAUTH_PROVIDERS)
    return response.data.providers
  },

  async getAuthConfig(): Promise<{ enable_local_auth: boolean; oauth_providers: OAuthProvider[] }> {
    const response = await apiClient.get<{ enable_local_auth: boolean; oauth_providers: OAuthProvider[] }>(API_ENDPOINTS.USER.AUTH_CONFIG)
    return response.data
  },

  async getProfile(): Promise<User> {
    const response = await apiClient.get<User>(API_ENDPOINTS.USER.PROFILE)
    return response.data
  },

  async updateProfile(data: ProfileUpdateData): Promise<User> {
    const response = await apiClient.put<User>(API_ENDPOINTS.USER.PROFILE, data)
    return response.data
  },

  async changePassword(data: PasswordChangeData): Promise<void> {
    await apiClient.put(API_ENDPOINTS.USER.CHANGE_PASSWORD, data)
  },

  async linkOAuthAccount(provider: string, data: LinkOAuthData): Promise<User> {
    const response = await apiClient.post<User>(API_ENDPOINTS.USER.LINK_OAUTH(provider), data)
    return response.data
  },

  async setLoginCredentials(data: SetCredentialsData): Promise<User> {
    const response = await apiClient.post<User>(API_ENDPOINTS.USER.SET_CREDENTIALS, data)
    return response.data
  },

  async uploadAvatar(file: File): Promise<{ avatar_url: string; message: string }> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post<{ avatar_url: string; message: string }>(
      `${API_ENDPOINTS.USER.BASE}/avatar/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post(API_ENDPOINTS.USER.LOGOUT)
    } catch {
      // 登出接口失败不影响本地登出
    } finally {
      localStorage.removeItem('authToken')
      localStorage.removeItem('loginTime')
      localStorage.removeItem('rememberMe')
      window.location.href = '/login'
    }
  },

  // === API Key管理 ===
  async getApiKeys(params?: { page?: number; size?: number; include_inactive?: boolean }): Promise<ApiKeyListResponse> {
    const response = await apiClient.get<ApiKeyListResponse>(API_ENDPOINTS.USER.API_KEYS, { params })
    return response.data
  },

  async createApiKey(data: ApiKeyCreateData): Promise<ApiKeyCreateResponse> {
    const response = await apiClient.post<ApiKeyCreateResponse>(API_ENDPOINTS.USER.API_KEYS, data)
    return response.data
  },

  async updateApiKey(id: number, data: Partial<ApiKeyCreateData>): Promise<ApiKeyUpdateResponse> {
    const response = await apiClient.put<ApiKeyUpdateResponse>(`${API_ENDPOINTS.USER.API_KEYS}${id}`, data)
    return response.data
  },

  async deleteApiKey(id: number): Promise<void> {
    await apiClient.delete(`${API_ENDPOINTS.USER.API_KEYS}${id}`)
  },

  async getApiKeyUsage(id: number, params?: { limit?: number }): Promise<ApiKeyUsageResponse> {
    const response = await apiClient.get<ApiKeyUsageResponse>(`${API_ENDPOINTS.USER.API_KEYS}${id}/usage`, { params })
    return response.data
  },

  // === 团队管理 ===
  async getTeams(): Promise<{ teams: Team[] }> {
    const response = await apiClient.get<{ owned_teams: Team[]; member_teams: Team[] }>(`${API_ENDPOINTS.USER.TEAMS}/my`)
    // 合并owned_teams和member_teams,并添加my_role字段
    const owned = response.data.owned_teams.map(team => ({ ...team, my_role: 'owner' as const }))
    const member = response.data.member_teams.map(team => ({ ...team, my_role: team.my_role || 'member' as const }))
    return { teams: [...owned, ...member] }
  },

  async getTeam(id: number): Promise<TeamDetail> {
    const response = await apiClient.get<TeamDetail>(`${API_ENDPOINTS.USER.TEAMS}/${id}`)
    return response.data
  },

  async createTeam(data: TeamCreateData): Promise<Team> {
    const response = await apiClient.post<Team>(API_ENDPOINTS.USER.TEAMS, data)
    return response.data
  },

  async updateTeam(id: number, data: TeamUpdateData): Promise<Team> {
    const response = await apiClient.put<Team>(`${API_ENDPOINTS.USER.TEAMS}/${id}`, data)
    return response.data
  },

  async deleteTeam(id: number): Promise<void> {
    await apiClient.delete(`${API_ENDPOINTS.USER.TEAMS}/${id}`)
  },

  async addTeamMember(teamId: number, data: TeamMemberAddData): Promise<TeamMember> {
    const response = await apiClient.post<TeamMember>(`${API_ENDPOINTS.USER.TEAMS}/${teamId}/members`, data)
    return response.data
  },

  async removeTeamMember(teamId: number, userId: number): Promise<void> {
    await apiClient.delete(`${API_ENDPOINTS.USER.TEAMS}/${teamId}/members/${userId}`)
  },

  async batchAddTeamMembers(teamId: number, data: TeamBatchAddMembersRequest): Promise<TeamBatchAddMembersResponse> {
    const response = await apiClient.post<TeamBatchAddMembersResponse>(
      API_ENDPOINTS.USER.TEAM_BATCH_ADD_MEMBERS(teamId), data
    )
    return response.data
  },

  async updateTeamMember(teamId: number, userId: number, data: TeamMemberUpdateData): Promise<TeamMember> {
    const response = await apiClient.put<TeamMember>(`${API_ENDPOINTS.USER.TEAMS}/${teamId}/members/${userId}/role`, data)
    return response.data
  },

  async getTeamStats(teamId: number): Promise<TeamStats> {
    const response = await apiClient.get<TeamStats>(`${API_ENDPOINTS.USER.TEAMS}/${teamId}/statistics`)
    return response.data
  },

  // === 批量用户名精确匹配 ===
  async batchLookupUsers(identifiers: string[]): Promise<BatchLookupResponse> {
    const response = await apiClient.post<BatchLookupResponse>(API_ENDPOINTS.USER.BATCH_LOOKUP, {
      identifiers,
    })
    return response.data
  },

  // === 用户管理（管理员功能） ===
  async getUsers(params: UserListParams = {}): Promise<PaginatedResponse<User>> {
    const queryParams: Record<string, any> = {}

    if (params.page !== undefined) queryParams.page = params.page
    const resolvedPageSize = params.size ?? params.page_size
    if (resolvedPageSize !== undefined) queryParams.size = resolvedPageSize
    if (params.search) queryParams.search = params.search
    if (params.sort_by) queryParams.sort_by = params.sort_by
    if (params.sort_order) queryParams.sort_order = params.sort_order

    // Pass array parameters as arrays (Axios will serialize them correctly for FastAPI)
    if (params.role) queryParams.role = normalizeArrayParam(params.role)
    if (params.is_active !== undefined) queryParams.is_active = normalizeArrayParam(params.is_active)
    if (params.is_online !== undefined) queryParams.is_online = normalizeArrayParam(params.is_online)
    if (params.is_vip !== undefined) queryParams.is_vip = normalizeArrayParam(params.is_vip)
    if (params.has_logged_in !== undefined) queryParams.has_logged_in = normalizeArrayParam(params.has_logged_in)

    const response = await apiClient.get<PaginatedResponse<User>>(API_ENDPOINTS.USER.USERS, {
      params: queryParams,
    })

    return response.data
  },

  async getUser(id: number): Promise<User> {
    const response = await apiClient.get<User>(`${API_ENDPOINTS.USER.USERS}/${id}`)
    return response.data
  },

  async updateUserStatus(id: number, isActive: boolean): Promise<User> {
    const response = await apiClient.patch<User>(`${API_ENDPOINTS.USER.USERS}/${id}/status`, { is_active: isActive })
    return response.data
  },

  async updateUserRole(id: number, role: string): Promise<User> {
    const response = await apiClient.patch<User>(`${API_ENDPOINTS.USER.USERS}/${id}/role`, { role })
    return response.data
  },

  async updateUserVip(id: number, isVip: boolean): Promise<User> {
    const response = await apiClient.patch<User>(`${API_ENDPOINTS.USER.USERS}/${id}/vip`, { is_vip: isVip })
    return response.data
  },

  async deleteUser(id: number): Promise<void> {
    await apiClient.delete(`${API_ENDPOINTS.USER.USERS}/${id}`)
  },

  // === 权限管理 ===
  async grantPermission(data: PermissionGrantData): Promise<any> {
    const response = await apiClient.post(API_ENDPOINTS.USER.PERMISSIONS_GRANT, data)
    return response.data
  },

  async revokePermission(id: number): Promise<void> {
    await apiClient.delete(`${API_ENDPOINTS.USER.PERMISSIONS}/${id}`)
  },

  async checkPermission(data: PermissionCheckData): Promise<PermissionCheckResponse> {
    const response = await apiClient.post<PermissionCheckResponse>(API_ENDPOINTS.USER.PERMISSIONS_CHECK, data)
    return response.data
  },

  async getUserPermission(userId: number, params: { resource_type: string; resource_id: number }): Promise<UserPermissionResponse> {
    const response = await apiClient.get<UserPermissionResponse>(`${API_ENDPOINTS.USER.PERMISSIONS_USER}/${userId}`, { params })
    return response.data
  },

  async getTeamPermission(teamId: number, params: { resource_type: string; resource_id: number }): Promise<TeamPermissionResponse> {
    const response = await apiClient.get<TeamPermissionResponse>(`${API_ENDPOINTS.USER.PERMISSIONS_TEAM}/${teamId}`, { params })
    return response.data
  },
}
